import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService, {
  MabarEvent,
} from '@/services/communityService';
import { useCreateGameModal } from '@/components/community/useCreateGameModal';
import { LineupSlot, getParticipantEffectivePlayerNumber } from '@/components/community/createGameUtils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/shadcn/sheet';
import { Skeleton } from '@/components/shadcn/skeleton';
import { historyCanGoBack } from '@/utils/nativeNavigation';
import { hostPaths } from '@/utils/hostPaths';

export default function CreateGamePage() {
  const { slug, gameSlug } = useParams<{ slug: string; gameSlug?: string }>();
  const navigate = useNavigate();
  const statisticPath = slug ? hostPaths.mabar.analitik(slug) : hostPaths.mabar.list;
  const goBack = () => {
    if (historyCanGoBack()) {
      navigate(-1);
      return;
    }
    navigate(statisticPath);
  };

  const [event, setEvent] = useState<MabarEvent | null>(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [activeTab, setActiveTab] = useState<'home' | 'away'>('home');

  // Modal state for editing player_number (Nomor Jersey Bottom Sheet / Dialog)
  const [editingNumberSlot, setEditingNumberSlot] = useState<{
    team: 'home' | 'away';
    participantId: number;
    name: string;
    currentNumber: number | null;
  } | null>(null);
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
  const [customNumberInput, setCustomNumberInput] = useState<string>('');

  // Modal state for editing role/position
  const [editingRoleSlot, setEditingRoleSlot] = useState<{
    team: 'home' | 'away';
    participantId: number;
    name: string;
    currentRole: string;
  } | null>(null);

  const isSparring = !!event?.config?.is_sparring;
  const teamAName = event?.config?.teams?.[0]?.name || 'Tim A';
  const teamBName = event?.config?.teams?.[1]?.name || 'Tim B';

  const state = useCreateGameModal({
    isOpen: true,
    eventSlug: slug || '',
    eventSportCategorySlug: event?.sport_category?.slug || '',
    eventCustomForm: event?.custom_form,
    gameSlug,
    isSparring,
    teamAName,
    teamBName,
    onClose: goBack,
    onSuccess: () => navigate(statisticPath),
  });

  useEffect(() => {
    if (!slug) return;
    setLoadingEvent(true);
    communityService
      .getEventBySlug(slug)
      .then((data) => setEvent(data))
      .catch(() => setEvent(null))
      .finally(() => setLoadingEvent(false));
  }, [slug]);

  const openJerseyModal = (team: 'home' | 'away', slot: LineupSlot) => {
    const currentNum = slot.playerNumber ?? null;
    setEditingNumberSlot({
      team,
      participantId: slot.participantId,
      name: slot.name,
      currentNumber: currentNum,
    });
    setSelectedNumber(currentNum);
    setCustomNumberInput(currentNum !== null && currentNum !== undefined ? String(currentNum) : '');
  };

  const handleSavePlayerNumber = () => {
    if (!editingNumberSlot) return;
    let finalNum: number | null = selectedNumber;

    if (customNumberInput.trim() !== '') {
      const parsed = parseInt(customNumberInput.trim(), 10);
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 99) {
        finalNum = parsed;
      }
    }

    if (editingNumberSlot.team === 'home') {
      state.handlePlayerNumberChangeHome(editingNumberSlot.participantId, finalNum);
    } else {
      state.handlePlayerNumberChangeAway(editingNumberSlot.participantId, finalNum);
    }
    setEditingNumberSlot(null);
  };

  const handleSaveRole = (newRole: string) => {
    if (!editingRoleSlot) return;
    if (editingRoleSlot.team === 'home') {
      state.handleRoleChangeHome(editingRoleSlot.participantId, newRole);
    } else {
      state.handleRoleChangeAway(editingRoleSlot.participantId, newRole);
    }
    setEditingRoleSlot(null);
  };

  if (loadingEvent || state.loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-40 w-full rounded-2xl mb-6" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  const formatList = [5, 6, 7, 8, 9, 11];

  return (
    <div className="max-w-2xl mx-auto px-4 py-4 sm:py-6 pb-28 min-h-screen flex flex-col relative">
      {/* Top Bar / Navigation Header (Desktop only, mobile handled by MobileTopbar) */}
      <div className="hidden md:flex items-center gap-3 mb-5">
        <button
          type="button"
          onClick={goBack}
          className="w-9 h-9 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 flex items-center justify-center transition-colors cursor-pointer"
          aria-label="Kembali"
        >
          <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-bold text-[#0E1726]">
          {state.isEditMode ? 'Edit Game' : 'Buat Game Baru'}
        </h1>
      </div>

      {/* Card 1: Format Pertandingan */}
      <div className="bg-white rounded-2xl border border-[#E0E6ED] p-4 sm:p-5 shadow-2xs mb-5">
        <h2 className="text-base font-bold text-[#0E1726] mb-1">Format Pertandingan</h2>
        <p className="text-xs text-gray-500 mb-3">Jumlah Pemain *</p>

        <div className="grid grid-cols-3 gap-2.5 mb-3">
          {formatList.map((n) => {
            const isSelected = state.formatNumber === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => state.setFormatNumber(n)}
                className={`py-3 px-2 rounded-xl text-center transition-all cursor-pointer ${
                  isSelected
                    ? 'border-2 border-[#FC480D] bg-[#FFF5EE] text-[#FC480D] font-extrabold shadow-2xs'
                    : 'border border-gray-200 bg-white text-[#0E1726] font-bold hover:border-gray-300'
                }`}
              >
                <span className="text-sm">{n}v{n}</span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-500 font-medium bg-gray-50 p-2.5 rounded-xl border border-gray-100">
          <Icon icon="ph:users-bold" className="w-4 h-4 text-[#FC480D] shrink-0" />
          <span>
            {state.formatNumber} pemain per tim · total {state.formatNumber * 2} pemain di lapangan
          </span>
        </div>
      </div>

      {/* Card 2: Segmented Control (Tim A vs Tim B) */}
      <div className="bg-gray-100 p-1.5 rounded-2xl flex gap-1 mb-5">
        <button
          type="button"
          onClick={() => setActiveTab('home')}
          className={`flex-1 py-3 px-4 rounded-xl text-xs sm:text-sm transition-all cursor-pointer flex items-center justify-center gap-2 ${
            activeTab === 'home'
              ? 'bg-white shadow-2xs text-[#FC480D] font-extrabold'
              : 'text-gray-500 font-semibold hover:text-gray-700'
          }`}
        >
          <span className="truncate max-w-[120px]">{state.homeTeamName || 'Tim A'}</span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-bold shrink-0 ${
              activeTab === 'home'
                ? 'bg-orange-50 text-[#FC480D]'
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {state.homeLineup.length}/{state.formatNumber}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('away')}
          className={`flex-1 py-3 px-4 rounded-xl text-xs sm:text-sm transition-all cursor-pointer flex items-center justify-center gap-2 ${
            activeTab === 'away'
              ? 'bg-white shadow-2xs text-[#3B82F6] font-extrabold'
              : 'text-gray-500 font-semibold hover:text-gray-700'
          }`}
        >
          <span className="truncate max-w-[120px]">{state.awayTeamName || 'Tim B'}</span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-bold shrink-0 ${
              activeTab === 'away'
                ? 'bg-blue-50 text-[#3B82F6]'
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {state.awayLineup.length}/{state.formatNumber}
          </span>
        </button>
      </div>

      {/* Card 3: Active Team Panel */}
      <div className="bg-white rounded-2xl border border-[#E0E6ED] p-4 sm:p-5 shadow-2xs mb-5 flex-1">
        {activeTab === 'home' ? (
          /* TIM A PANEL */
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5 flex-1 max-w-[65%] min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#FC480D] text-white font-extrabold text-xs flex items-center justify-center shrink-0">
                  A
                </div>
                <div className="relative flex-1 min-w-0 flex items-center gap-1 group">
                  <input
                    type="text"
                    value={state.homeTeamName}
                    onChange={(e) => state.setHomeTeamName(e.target.value)}
                    placeholder="NAMA TIM A"
                    className="w-full font-bold text-sm text-[#0E1726] uppercase tracking-wide bg-transparent border-b border-gray-200 hover:border-[#FC480D] focus:border-[#FC480D] focus:bg-orange-50/20 px-1 py-0.5 outline-none transition-all rounded-xs"
                  />
                  <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5 text-gray-400 opacity-60 group-hover:opacity-100 shrink-0" />
                </div>
              </div>
              {state.homeSideMode !== 'choice' && (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#FFF5EE] text-[#FC480D]">
                  {state.homeSideMode === 'lineup'
                    ? 'Tim Baru'
                    : state.homeSideMode === 'saved_list'
                    ? 'Pilih Tim Tersimpan'
                    : 'Generate Otomatis'}
                </span>
              )}
            </div>

            {/* CHOICE MODE */}
            {state.homeSideMode === 'choice' && (
              <div>
                <p className="text-xs font-bold text-[#0E1726] mb-3">Cara Isi Tim *</p>
                <div className="space-y-2.5">
                  <button
                    type="button"
                    onClick={state.onChoiceTimBaruHome}
                    className="w-full p-4 rounded-xl border border-gray-200 hover:border-[#FC480D] bg-white hover:bg-[#FFF5EE]/50 transition-all flex items-center justify-between text-left cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                        <Icon icon="ph:users-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0E1726]">Tim Baru</p>
                        <p className="text-xs text-gray-500">Pilih pemain satu per satu dari daftar</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-5 h-5 text-gray-400 group-hover:text-[#FC480D]" />
                  </button>

                  <button
                    type="button"
                    onClick={() => state.setHomeSideMode('saved_list')}
                    className="w-full p-4 rounded-xl border border-gray-200 hover:border-[#FC480D] bg-white hover:bg-[#FFF5EE]/50 transition-all flex items-center justify-between text-left cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                        <Icon icon="ph:list-bullets-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0E1726]">Pilih Tim Tersimpan</p>
                        <p className="text-xs text-gray-500">Gunakan susunan tim dari game sebelumnya</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-5 h-5 text-gray-400 group-hover:text-[#FC480D]" />
                  </button>

                  <button
                    type="button"
                    onClick={state.handleGenerateHome}
                    className="w-full p-4 rounded-xl border border-gray-200 hover:border-[#FC480D] bg-white hover:bg-[#FFF5EE]/50 transition-all flex items-center justify-between text-left cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                        <Icon icon="ph:sparkle-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0E1726]">Generate Otomatis</p>
                        <p className="text-xs text-gray-500">Buat tim seimbang secara otomatis</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-5 h-5 text-gray-400 group-hover:text-[#FC480D]" />
                  </button>
                </div>
                {state.generateErrorHome && (
                  <p className="text-xs text-red-500 font-semibold mt-3">{state.generateErrorHome}</p>
                )}
              </div>
            )}

            {/* SAVED LIST MODE */}
            {state.homeSideMode === 'saved_list' && (
              <div>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl mb-4 border border-gray-100">
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                    <Icon icon="ph:list-bullets-bold" className="w-4 h-4 text-[#FC480D]" />
                    <span>Cara isi tim: Pilih Tim Tersimpan</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => state.setHomeSideMode('choice')}
                    className="text-xs font-bold text-[#FC480D] bg-white border border-orange-200 px-3 py-1 rounded-lg hover:bg-orange-50 cursor-pointer"
                  >
                    Ubah
                  </button>
                </div>

                <p className="text-xs font-bold text-[#0E1726] mb-3">Tim Tersimpan</p>
                {state.savedTeamsForHome.length === 0 ? (
                  <p className="text-xs text-gray-400 py-6 text-center">Belum ada data tim tersimpan</p>
                ) : (
                  <div className="space-y-2.5">
                    {state.savedTeamsForHome.map((team, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => state.handleSelectSavedTeamHome(team.teamName, team.lineup)}
                        className="w-full p-3.5 rounded-xl border border-gray-200 hover:border-[#FC480D] bg-white flex items-center justify-between text-left cursor-pointer transition-all"
                      >
                        <div>
                          <p className="text-sm font-bold text-[#0E1726]">{team.teamName}</p>
                          <p className="text-xs text-gray-500">{team.lineup.length} pemain</p>
                        </div>
                        <Icon icon="ph:circle-bold" className="w-5 h-5 text-gray-300" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* LINEUP MODE */}
            {state.homeSideMode === 'lineup' && (
              <div>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl mb-4 border border-gray-100">
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                    <Icon icon="ph:users-bold" className="w-4 h-4 text-[#FC480D]" />
                    <span>Cara isi tim: Tim Baru</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => state.setHomeSideMode('choice')}
                    className="text-xs font-bold text-[#FC480D] bg-white border border-orange-200 px-3 py-1 rounded-lg hover:bg-orange-50 cursor-pointer"
                  >
                    Ubah
                  </button>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-[#0E1726]">Pilih Pemain</span>
                  <span
                    className={`text-xs font-extrabold ${
                      state.homeLineup.length === state.formatNumber
                        ? 'text-emerald-600'
                        : 'text-[#FC480D]'
                    }`}
                  >
                    {state.homeLineup.length}/{state.formatNumber} pemain dipilih
                  </span>
                </div>

                {/* Search Bar */}
                <div className="relative mb-3">
                  <Icon
                    icon="ph:magnifying-glass-bold"
                    className="w-4 h-4 text-gray-400 absolute left-3 top-3"
                  />
                  <input
                    type="text"
                    value={state.searchHome}
                    onChange={(e) => state.setSearchHome(e.target.value)}
                    placeholder="Cari nama pemain"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-xs font-medium focus:outline-none focus:border-[#FC480D]"
                  />
                </div>

                {/* Player List */}
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                  {/* Render Selected Players First */}
                  {state.homeLineup.map((slot) => {
                    const playedText = state.getPlayedLabel(slot.participantId);
                    return (
                      <div
                        key={slot.participantId}
                        onClick={() => state.removeFromTeam('home', slot.participantId)}
                        className="p-3 rounded-xl border-2 border-[#FC480D] bg-[#FFF5EE] flex items-center justify-between gap-3 transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {/* Clickable Jersey Icon to Open Bottom Sheet Modal */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openJerseyModal('home', slot);
                            }}
                            className="relative w-9 h-10 flex items-center justify-center shrink-0 active:scale-95 transition-transform cursor-pointer"
                            title="Klik untuk ubah nomor jersey"
                          >
                            <Icon icon="ph:t-shirt-fill" className="w-9 h-9 text-[#FC480D]" />
                            <span className="absolute text-[11px] font-black text-white top-2.5">
                              {slot.playerNumber ?? '#'}
                            </span>
                          </button>

                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-[#0E1726] truncate">{slot.name}</p>
                            <p className="text-[11px] text-gray-500 font-medium">{playedText}</p>
                          </div>
                        </div>

                        {/* Position Badge & Selection Checkmark */}
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingRoleSlot({
                                team: 'home',
                                participantId: slot.participantId,
                                name: slot.name,
                                currentRole: slot.role,
                              });
                            }}
                            className={`px-2.5 py-1 rounded-lg text-xs font-extrabold border cursor-pointer ${
                              slot.role === 'GK'
                                ? 'bg-amber-50 text-amber-600 border-amber-200'
                                : slot.role === 'DF'
                                ? 'bg-blue-50 text-blue-600 border-blue-200'
                                : slot.role === 'MF'
                                ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                : slot.role === 'FW'
                                ? 'bg-pink-50 text-pink-600 border-pink-200'
                                : 'bg-gray-100 text-gray-600 border-gray-200'
                            }`}
                          >
                            {slot.role || 'POS'}
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              state.removeFromTeam('home', slot.participantId);
                            }}
                            className="w-6 h-6 rounded-full bg-[#FC480D] text-white flex items-center justify-center cursor-pointer shadow-2xs"
                          >
                            <Icon icon="ph:check-bold" className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Render Unselected Players */}
                  {state.availableForHome.map((p) => {
                    const pid = typeof p.id === 'string' ? parseInt(p.id, 10) : p.id;
                    const playedText = state.getPlayedLabel(pid);
                    return (
                      <div
                        key={pid}
                        onClick={() => state.addToTeam('home', p, true)}
                        className="p-3 rounded-xl border border-gray-200 bg-white hover:border-gray-300 flex items-center justify-between gap-3 transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="relative w-9 h-10 flex items-center justify-center shrink-0">
                            <Icon icon="ph:t-shirt-fill" className="w-9 h-9 text-gray-300" />
                            <span className="absolute text-[11px] font-black text-white top-2.5">
                              {getParticipantEffectivePlayerNumber(p) ?? '#'}
                            </span>
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-[#0E1726] truncate">{p.name}</p>
                            <p className="text-[11px] text-gray-500 font-medium">{playedText}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-gray-100 text-gray-500">
                            {p.role || p.position || 'N'}
                          </span>
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* TIM B PANEL */
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5 flex-1 max-w-[65%] min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#3B82F6] text-white font-extrabold text-xs flex items-center justify-center shrink-0">
                  B
                </div>
                <div className="relative flex-1 min-w-0 flex items-center gap-1 group">
                  <input
                    type="text"
                    value={state.awayTeamName}
                    onChange={(e) => state.setAwayTeamName(e.target.value)}
                    placeholder="NAMA TIM B"
                    className="w-full font-bold text-sm text-[#0E1726] uppercase tracking-wide bg-transparent border-b border-gray-200 hover:border-[#3B82F6] focus:border-[#3B82F6] focus:bg-blue-50/20 px-1 py-0.5 outline-none transition-all rounded-xs"
                  />
                  <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5 text-gray-400 opacity-60 group-hover:opacity-100 shrink-0" />
                </div>
              </div>
              {state.awaySideMode !== 'choice' && (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-[#3B82F6]">
                  {state.awaySideMode === 'lineup'
                    ? 'Tim Baru'
                    : state.awaySideMode === 'saved_list'
                    ? 'Pilih Tim Tersimpan'
                    : 'Generate Otomatis'}
                </span>
              )}
            </div>

            {/* CHOICE MODE */}
            {state.awaySideMode === 'choice' && (
              <div>
                <p className="text-xs font-bold text-[#0E1726] mb-3">Cara Isi Tim *</p>
                <div className="space-y-2.5">
                  <button
                    type="button"
                    onClick={state.onChoiceTimBaruAway}
                    className="w-full p-4 rounded-xl border border-gray-200 hover:border-[#3B82F6] bg-white hover:bg-blue-50/50 transition-all flex items-center justify-between text-left cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#3B82F6] flex items-center justify-center shrink-0">
                        <Icon icon="ph:users-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0E1726]">Tim Baru</p>
                        <p className="text-xs text-gray-500">Pilih pemain satu per satu dari daftar</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-5 h-5 text-gray-400 group-hover:text-[#3B82F6]" />
                  </button>

                  <button
                    type="button"
                    onClick={() => state.setAwaySideMode('saved_list')}
                    className="w-full p-4 rounded-xl border border-gray-200 hover:border-[#3B82F6] bg-white hover:bg-blue-50/50 transition-all flex items-center justify-between text-left cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#3B82F6] flex items-center justify-center shrink-0">
                        <Icon icon="ph:list-bullets-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0E1726]">Pilih Tim Tersimpan</p>
                        <p className="text-xs text-gray-500">Gunakan susunan tim dari game sebelumnya</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-5 h-5 text-gray-400 group-hover:text-[#3B82F6]" />
                  </button>

                  <button
                    type="button"
                    onClick={state.handleGenerateAway}
                    className="w-full p-4 rounded-xl border border-gray-200 hover:border-[#3B82F6] bg-white hover:bg-blue-50/50 transition-all flex items-center justify-between text-left cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#3B82F6] flex items-center justify-center shrink-0">
                        <Icon icon="ph:sparkle-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0E1726]">Generate Otomatis</p>
                        <p className="text-xs text-gray-500">Buat tim seimbang secara otomatis</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-5 h-5 text-gray-400 group-hover:text-[#3B82F6]" />
                  </button>
                </div>
                {state.generateErrorAway && (
                  <p className="text-xs text-red-500 font-semibold mt-3">{state.generateErrorAway}</p>
                )}
              </div>
            )}

            {/* SAVED LIST MODE */}
            {state.awaySideMode === 'saved_list' && (
              <div>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl mb-4 border border-gray-100">
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                    <Icon icon="ph:list-bullets-bold" className="w-4 h-4 text-[#3B82F6]" />
                    <span>Cara isi tim: Pilih Tim Tersimpan</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => state.setAwaySideMode('choice')}
                    className="text-xs font-bold text-[#3B82F6] bg-white border border-blue-200 px-3 py-1 rounded-lg hover:bg-blue-50 cursor-pointer"
                  >
                    Ubah
                  </button>
                </div>

                <p className="text-xs font-bold text-[#0E1726] mb-3">Tim Tersimpan</p>
                {state.savedTeamsForAway.length === 0 ? (
                  <p className="text-xs text-gray-400 py-6 text-center">Belum ada data tim tersimpan</p>
                ) : (
                  <div className="space-y-2.5">
                    {state.savedTeamsForAway.map((team, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => state.handleSelectSavedTeamAway(team.teamName, team.lineup)}
                        className="w-full p-3.5 rounded-xl border border-gray-200 hover:border-[#3B82F6] bg-white flex items-center justify-between text-left cursor-pointer transition-all"
                      >
                        <div>
                          <p className="text-sm font-bold text-[#0E1726]">{team.teamName}</p>
                          <p className="text-xs text-gray-500">{team.lineup.length} pemain</p>
                        </div>
                        <Icon icon="ph:circle-bold" className="w-5 h-5 text-gray-300" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* LINEUP MODE */}
            {state.awaySideMode === 'lineup' && (
              <div>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl mb-4 border border-gray-100">
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                    <Icon icon="ph:users-bold" className="w-4 h-4 text-[#3B82F6]" />
                    <span>Cara isi tim: Tim Baru</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => state.setAwaySideMode('choice')}
                    className="text-xs font-bold text-[#3B82F6] bg-white border border-blue-200 px-3 py-1 rounded-lg hover:bg-blue-50 cursor-pointer"
                  >
                    Ubah
                  </button>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-[#0E1726]">Pilih Pemain</span>
                  <span
                    className={`text-xs font-extrabold ${
                      state.awayLineup.length === state.formatNumber
                        ? 'text-emerald-600'
                        : 'text-[#3B82F6]'
                    }`}
                  >
                    {state.awayLineup.length}/{state.formatNumber} pemain dipilih
                  </span>
                </div>

                {/* Search Bar */}
                <div className="relative mb-3">
                  <Icon
                    icon="ph:magnifying-glass-bold"
                    className="w-4 h-4 text-gray-400 absolute left-3 top-3"
                  />
                  <input
                    type="text"
                    value={state.searchAway}
                    onChange={(e) => state.setSearchAway(e.target.value)}
                    placeholder="Cari nama pemain"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-xs font-medium focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>

                {/* Player List */}
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                  {/* Render Selected Players */}
                  {state.awayLineup.map((slot) => {
                    const playedText = state.getPlayedLabel(slot.participantId);
                    return (
                      <div
                        key={slot.participantId}
                        onClick={() => state.removeFromTeam('away', slot.participantId)}
                        className="p-3 rounded-xl border-2 border-[#3B82F6] bg-[#EFF6FF] flex items-center justify-between gap-3 transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {/* Clickable Jersey Icon to Open Bottom Sheet Modal */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openJerseyModal('away', slot);
                            }}
                            className="relative w-9 h-10 flex items-center justify-center shrink-0 active:scale-95 transition-transform cursor-pointer"
                            title="Klik untuk ubah nomor jersey"
                          >
                            <Icon icon="ph:t-shirt-fill" className="w-9 h-9 text-[#3B82F6]" />
                            <span className="absolute text-[11px] font-black text-white top-2.5">
                              {slot.playerNumber ?? '#'}
                            </span>
                          </button>

                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-[#0E1726] truncate">{slot.name}</p>
                            <p className="text-[11px] text-gray-500 font-medium">{playedText}</p>
                          </div>
                        </div>

                        {/* Position Badge & Selection Checkmark */}
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingRoleSlot({
                                team: 'away',
                                participantId: slot.participantId,
                                name: slot.name,
                                currentRole: slot.role,
                              });
                            }}
                            className={`px-2.5 py-1 rounded-lg text-xs font-extrabold border cursor-pointer ${
                              slot.role === 'GK'
                                ? 'bg-amber-50 text-amber-600 border-amber-200'
                                : slot.role === 'DF'
                                ? 'bg-blue-50 text-blue-600 border-blue-200'
                                : slot.role === 'MF'
                                ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                : slot.role === 'FW'
                                ? 'bg-pink-50 text-pink-600 border-pink-200'
                                : 'bg-gray-100 text-gray-600 border-gray-200'
                            }`}
                          >
                            {slot.role || 'POS'}
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              state.removeFromTeam('away', slot.participantId);
                            }}
                            className="w-6 h-6 rounded-full bg-[#3B82F6] text-white flex items-center justify-center cursor-pointer shadow-2xs"
                          >
                            <Icon icon="ph:check-bold" className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Render Unselected Players */}
                  {state.availableForAway.map((p) => {
                    const pid = typeof p.id === 'string' ? parseInt(p.id, 10) : p.id;
                    const playedText = state.getPlayedLabel(pid);
                    return (
                      <div
                        key={pid}
                        onClick={() => state.addToTeam('away', p, true)}
                        className="p-3 rounded-xl border border-gray-200 bg-white hover:border-gray-300 flex items-center justify-between gap-3 transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="relative w-9 h-10 flex items-center justify-center shrink-0">
                            <Icon icon="ph:t-shirt-fill" className="w-9 h-9 text-gray-300" />
                            <span className="absolute text-[11px] font-black text-white top-2.5">
                              {getParticipantEffectivePlayerNumber(p) ?? '#'}
                            </span>
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-[#0E1726] truncate">{p.name}</p>
                            <p className="text-[11px] text-gray-500 font-medium">{playedText}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-gray-100 text-gray-500">
                            {p.role || p.position || 'N'}
                          </span>
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {state.error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-bold mb-4">
          {state.error}
        </div>
      )}

      {/* Fixed Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 p-3 sm:p-4 shadow-lg">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={state.resetForm}
            className="px-5 py-3 rounded-full border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            Reset
          </button>

          <button
            type="button"
            disabled={!state.canSave || state.saving}
            onClick={state.handleSave}
            className={`flex-1 py-3 px-6 rounded-full text-xs sm:text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-md cursor-pointer ${
              state.canSave && !state.saving
                ? 'bg-gradient-to-r from-[#FF8552] to-[#FC480D] text-white hover:shadow-lg active:scale-98'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
            }`}
          >
            {state.saving ? (
              <>
                <Icon icon="ph:spinner-bold" className="w-4 h-4 animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <Icon icon="ph:check-bold" className="w-4 h-4" />
                <span>Simpan</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Responsive Bottom Sheet / Dialog for Edit Nomor Jersey */}
      {editingNumberSlot && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4 animate-in fade-in duration-200"
          onClick={() => setEditingNumberSlot(null)}
        >
          <div
            className="w-full max-w-md bg-white rounded-t-3xl md:rounded-3xl p-5 shadow-2xl animate-in slide-in-from-bottom md:zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Handle Bar */}
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4 md:hidden" />

            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-extrabold text-[#0E1726]">Nomor Jersey</h3>
                <p className="text-xs text-gray-500 font-medium">
                  {editingNumberSlot.name} · {editingNumberSlot.currentNumber !== null && editingNumberSlot.currentNumber !== undefined ? `saat ini ${editingNumberSlot.currentNumber}` : 'belum ada nomor'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingNumberSlot(null)}
                className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center cursor-pointer transition-colors"
                aria-label="Tutup"
              >
                <Icon icon="ph:x-bold" className="w-4 h-4" />
              </button>
            </div>

            {/* Quick 1-20 Number Grid (7 Columns) */}
            <div className="grid grid-cols-7 gap-2 mb-5">
              {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => {
                const isSelected = selectedNumber === num;
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => {
                      setSelectedNumber(num);
                      setCustomNumberInput(String(num));
                    }}
                    className={`h-11 rounded-xl text-sm font-extrabold transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-[#FC480D] text-white shadow-sm scale-105'
                        : 'bg-gray-50 text-[#0E1726] border border-gray-100 hover:bg-gray-100'
                    }`}
                  >
                    {num}
                  </button>
                );
              })}
            </div>

            {/* Custom Input & Action Button */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <span className="absolute left-3.5 top-3.5 text-xs font-bold text-gray-400">#</span>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={customNumberInput}
                  onChange={(e) => {
                    const valStr = e.target.value;
                    setCustomNumberInput(valStr);
                    if (valStr.trim() !== '') {
                      const parsed = parseInt(valStr.trim(), 10);
                      if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 99) {
                        setSelectedNumber(parsed);
                      } else {
                        setSelectedNumber(null);
                      }
                    } else {
                      setSelectedNumber(null);
                    }
                  }}
                  placeholder="Nomor lain"
                  className="w-full pl-8 pr-3 py-3 rounded-2xl border border-gray-200 text-sm font-medium focus:outline-none focus:border-[#FC480D]"
                />
              </div>

              <button
                type="button"
                onClick={handleSavePlayerNumber}
                className="px-7 py-3 rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-xs font-extrabold shadow-md cursor-pointer transition-all active:scale-95 shrink-0"
              >
                Pakai
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Sheet Edit Posisi / Role */}
      <Sheet
        open={editingRoleSlot !== null}
        onOpenChange={(open) => !open && setEditingRoleSlot(null)}
      >
        <SheetContent
          side="bottom"
          className="rounded-t-3xl border-t border-gray-100 max-h-[90vh] overflow-y-auto px-5 pt-4 pb-12"
        >
          <div className="w-12 h-1 bg-gray-200 rounded-full mx-auto mb-3 shrink-0" />
          <SheetHeader className="text-left mb-2">
            <SheetTitle className="text-base font-bold text-[#0E1726]">
              Pilih Posisi Pemain
            </SheetTitle>
          </SheetHeader>
          <div className="pt-2 pb-6 space-y-2">
            <p className="text-xs text-gray-500 font-medium mb-3">
              Pilih posisi untuk <strong className="text-gray-900">{editingRoleSlot?.name}</strong>:
            </p>
            {[
              { code: 'GK', name: 'Goalkeeper (Kiper)', bg: 'bg-amber-50 text-amber-600 border-amber-200' },
              { code: 'DF', name: 'Defender (Bek)', bg: 'bg-blue-50 text-blue-600 border-blue-200' },
              { code: 'MF', name: 'Midfielder (Gelandang)', bg: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
              { code: 'FW', name: 'Forward (Penyerang)', bg: 'bg-pink-50 text-pink-600 border-pink-200' },
              { code: 'N', name: 'Tanpa Posisi', bg: 'bg-gray-50 text-gray-600 border-gray-200' },
            ].map((roleItem) => (
              <button
                key={roleItem.code}
                type="button"
                onClick={() => handleSaveRole(roleItem.code)}
                className={`w-full p-3 rounded-xl border text-left flex items-center justify-between cursor-pointer transition-all ${
                  editingRoleSlot?.currentRole === roleItem.code
                    ? 'border-[#FC480D] bg-[#FFF5EE]'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <span className="text-xs font-bold text-[#0E1726]">{roleItem.name}</span>
                <span className={`px-2 py-0.5 rounded-md text-xs font-extrabold border ${roleItem.bg}`}>
                  {roleItem.code}
                </span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
